import Map from "ol/Map";
import VectorLayer from "ol/layer/Vector";
import TileLayer from "ol/layer/Tile.js";
import TileWMS from "ol/source/TileWMS.js";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import GeoJSON from "ol/format/GeoJSON";
import Control from "ol/control/Control";
import Style from "ol/style/Style";
import Stroke from "ol/style/Stroke";
import Fill from "ol/style/Fill";
import FullScreen from "ol/control/FullScreen";
import { transform } from "ol/proj";
import { listen } from "ol/events";
import EventType from "ol/events/EventType";
import { CLASS_CONTROL, CLASS_UNSELECTABLE } from "ol/css";

import moment from "moment-timezone";
import { color as d3color } from "d3-color";

import TimeLayer from "./TimeLayer/TimeLayer";
import NysmMapSetLayer from "./TimeLayer/NysmMapSetLayer";
import NowCoastLayer from "./TimeLayer/NowCoastLayer";
import NysmApiLayer from "./TimeLayer/NysmApiLayer";
// import NysmApiWindGridLayer from "./TimeLayer/NysmApiWindGridLayer";
import NysmLightningLayer from "./TimeLayer/NysmLightningLayer";
import NysmDailyLayer from "./TimeLayer/NysmDailyLayer";
import MbGlLayerFactory from "./Layers/MapboxGlLayerFactory";
import NoaaMapServicesBREF from "./TimeLayer/NoaaMapServicesBREF";
import NysmFreezingLineLayer from "./TimeLayer/NysmFreezingLineLayer";

class NyZoomControlMenu extends Control {
  constructor(opt_options) {
    const options = opt_options || {};
    super({
      element: document.createElement("div"),
      target: options.target,
    });

    if (!options.mapProjection) {
      throw Error("Must pass in target map projection");
    }
    this.mapProjection = options.mapProjection;

    // Regions looks like:
    // [
    //   {label: "New York State", extents: [[-79.79, 40.48], [-71.84, 45.02]]},
    //   {label: "New York City", extents: [[-74.26, 40.48], [-73.6, 41]]}
    // ]
    if (!options.regions) {
      throw Error("Must pass in regions array");
    }
    const regions = options.regions;

    const className = "ol-ny-zoom-menu";
    const dropdownMenuElement = document.createElement("div");
    this.menuElement = dropdownMenuElement;
    dropdownMenuElement.style.display = "none";
    this._isOpen = false;
    const ul = document.createElement("ul");
    regions.forEach((r) => {
      const li = document.createElement("li");
      li.appendChild(document.createTextNode(r.label));
      listen(li, EventType.CLICK, this.handleClick_.bind(this, r.extents));
      ul.appendChild(li);
    });
    dropdownMenuElement.appendChild(ul);

    const cssClasses =
      className + " " + CLASS_UNSELECTABLE + " " + CLASS_CONTROL;
    const element = this.element;
    element.className = cssClasses;
    element.appendChild(dropdownMenuElement);
  }

  handleClick_(extent, event) {
    event.preventDefault();
    const map = this.getMap();
    const view = map.getView();
    if (!view) {
      return;
    }
    const projExtent = transform(
      extent[0],
      NysmMap.latlonProjection,
      this.mapProjection
    ).concat(
      transform(extent[1], NysmMap.latlonProjection, this.mapProjection)
    );
    view.setConstrainResolution(false);
    view.fit(projExtent, { duration: 750 });
    this.hide();
  }

  hide() {
    this.menuElement.style.display = "none";
    this._isOpen = false;
  }

  show() {
    this.menuElement.style.display = "block";
    this._isOpen = true;
  }

  get open() {
    return this._isOpen;
  }
}

class NyZoomControl extends Control {
  constructor(opt_options) {
    const options = opt_options || {};
    super({
      element: document.createElement("div"),
      target: options.target,
    });

    if (!options.mapProjection) {
      throw Error("Must pass in target map projection");
    }
    this.mapProjection = options.mapProjection;

    // Regions looks like:
    // [
    //   {label: "New York State", extents: [[-79.79, 40.48], [-71.84, 45.02]]},
    //   {label: "New York City", extents: [[-74.26, 40.48], [-73.6, 41]]}
    // ]
    if (!options.regions) {
      throw Error("Must pass in regions array");
    }
    const regions = options.regions;

    const className = "ol-ny-zoom";
    const dropdownElement = document.createElement("button");
    // element.className = className + "-dropdown";
    dropdownElement.setAttribute("type", "button");
    dropdownElement.title = "Regions";
    dropdownElement.appendChild(document.createTextNode("Regions"));
    let dropdownMenu = null;
    listen(dropdownElement, EventType.CLICK, () => {
      if (!dropdownMenu) {
        dropdownMenu = new NyZoomControlMenu({
          mapProjection: this.mapProjection,
          regions,
        });
        this.getMap().addControl(dropdownMenu);
      }

      if (dropdownMenu.open) {
        dropdownMenu.hide();
      } else {
        dropdownMenu.show();
      }
    });

    const cssClasses =
      className + " " + CLASS_UNSELECTABLE + " " + CLASS_CONTROL;
    const element = this.element;
    element.className = cssClasses;
    element.appendChild(dropdownElement);
  }
}

export default class NysmMap {
  static sourceCache = {};

  // Create Map from map_options using a div within the target specified in options.
  constructor(map_options, options) {
    let my_map_options = Object.assign({}, map_options);
    my_map_options = Object.assign(my_map_options, { target: options.target });
    this._map = new Map(my_map_options);
    this._mapsets = {};
    this._timeManager = options.hasOwnProperty("timeManager")
      ? options.timeManager
      : null;
    this.addLayer = this.addLayer.bind(this);
    this.removeLayer = this.removeLayer.bind(this);
    this.fadeLayer = this.fadeLayer.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this._legendTarget = options.legendTarget;

    this._duration = moment.duration(185, "minutes");

    // Fader objects should be:
    // {
    //   layer: layer (not an ol_layer) begin faded,
    //   finish: function with no args that immediately completes the fade
    // }
    this._faders = [];

    this._cleanups = [() => this._map.setTarget(null)];
    this._resizeActions = [() => this._map.updateSize()];
  }

  addLayer(layer) {
    this._clearFaders(layer);
    if (layer.hasOwnProperty("ol_layer")) {
      if (layer.group.getLayers().getArray().indexOf(layer.ol_layer) === -1) {
        layer.group.getLayers().push(layer.ol_layer);
        if (layer.ol_layer instanceof TimeLayer) {
          layer.ol_layer.addedToMap();
        }
        if (this._legendTarget && layer.ol_layer.get("legend")) {
          layer.ol_layer.get("legend").draw(this._legendTarget);
        }
      }
      return;
    }
    if (layer.type === "vector-geojson") {
      let source = null;
      if (NysmMap.sourceCache.hasOwnProperty(layer.url)) {
        source = NysmMap.sourceCache[layer.url];
      } else {
        source = new VectorSource({
          url: layer.url,
          format: new GeoJSON(),
        });
        if (layer.cacheUrl) {
          NysmMap.sourceCache[layer.url] = source;
          // Add cache to layers and verify this works.
        }
      }
      var ol_layer = new VectorLayer({
        title: layer.title,
        source: source,
        style: layer.style,
        updateWhileAnimating: true,
        updateWhileInteracting: true,
      });
      layer.ol_layer = ol_layer;
      layer.group.getLayers().push(layer.ol_layer);
    } else if (layer.type === "nysm-map-grad") {
      let time_layer = new NysmMapSetLayer({
        title: layer.title,
        visible: true,
        timeManager: this.timeManager,
        dataset: layer.dataset,
        mapset: layer.mapset,
        mapid: layer.mapid,
        mapProjection: this.mapProjection,
        type: NysmMapSetLayer.types.gradient,
        subLayerOptions: {
          title: layer.title,
          units: layer.units,
        },
      });
      layer.group.getLayers().push(time_layer);
      time_layer.addedToMap();
      this._legendTarget && time_layer.get("legend").draw(this._legendTarget);
      layer.ol_layer = time_layer;
    } else if (layer.type === "nysm-map-obs") {
      let time_layer = new NysmMapSetLayer({
        title: layer.title,
        visible: true,
        timeManager: this.timeManager,
        dataset: layer.dataset,
        mapset: layer.mapset,
        mapid: layer.mapid,
        mapProjection: this.mapProjection,
        type: NysmMapSetLayer.types.observations,
        subLayerOptions: {
          title: layer.title,
          hotspot: layer.hotspot,
          units: layer.units,
          precision: layer.precision,
          font: layer.font,
          fill: layer.fill,
          stroke: layer.stroke,
        },
      });
      layer.group.getLayers().push(time_layer);
      time_layer.addedToMap();
      layer.ol_layer = time_layer;
    } else if (layer.type === "nysm-api-obs") {
      const time_layer = new NysmApiLayer({
        title: layer.title,
        visible: true,
        timeManager: this.timeManager,
        dataset: layer.dataset,
        subset: layer.subset,
        varid: layer.varid,
        stations: layer.stations,
        mapProjection: this.mapProjection,
        layerMakerCallback: layer.layerMakerCallback,
      });
      layer.group.getLayers().push(time_layer);
      time_layer.addedToMap();
      layer.ol_layer = time_layer;
    } else if (layer.type === "nysm-daily") {
      const time_layer = new NysmDailyLayer({
        title: layer.title,
        visible: true,
        timeManager: this.timeManager,
        timezone: layer.timezone,
        mapProjection: this.mapProjection,
        layerMakerCallback: layer.layerMakerCallback,
      });
      layer.group.getLayers().push(time_layer);
      time_layer.addedToMap();
      layer.ol_layer = time_layer;
    } else if (layer.type === "nysm-freezing-line") {
      const time_layer = new NysmFreezingLineLayer({
        title: layer.title,
        visible: true,
        timeManager: this.timeManager,
        mapProjection: this.mapProjection
      });
      layer.group.getLayers().push(time_layer);
      time_layer.addedToMap();
      layer.ol_layer = time_layer;
    } else if (layer.type === "nysm-lightning") {
      const time_layer = new NysmLightningLayer({
        title: layer.title,
        visible: true,
        timeManager: this.timeManager,
        mapProjection: this.mapProjection
      });
      layer.group.getLayers().push(time_layer);
      time_layer.addedToMap();
      layer.ol_layer = time_layer;
    } else if (layer.type === "nowcoast") {
      const time_layer = new NowCoastLayer({
        title: layer.title,
        visible: true,
        timeManager: this.timeManager,
        service: layer.service,
        layerid: layer.layerid,
      });
      layer.group.getLayers().push(time_layer);
      time_layer.addedToMap();
      layer.ol_layer = time_layer;
      this._legendTarget && time_layer.get("legend").draw(this._legendTarget);
    } else if (layer.type === "noaa-mapservices-bref") {
      const time_layer = new NoaaMapServicesBREF({
        title: layer.title,
        visible: true,
        timeManager: this.timeManager,
      });
      layer.group.getLayers().push(time_layer);
      time_layer.addedToMap();
      layer.ol_layer = time_layer;
      this._legendTarget && time_layer.get("legend").draw(this._legendTarget);
    } else if (layer.type === "wmst") {
      const time_layer = new TimeLayer({
        title: layer.title,
        visible: true,
        timeManager: this.timeManager,
      });
      time_layer.addedToMap();
      layer.ol_layer = time_layer;
      layer.group.getLayers().push(time_layer);

      // Fetch times and add frames to the time layer.
      let endTime = moment.tz("UTC");
      endTime.minutes(
        endTime.toObject().minutes - (endTime.toObject().minutes % 5)
      );
      const startTime = endTime.clone().subtract(this._duration);

      for (let t = startTime; t <= endTime; t.add(moment.duration(5, "m"))) {
        let formattedTime = t.format("YYYY-MM-DD") + "T" + t.format("HH:mm");
        let params = Object.assign({}, layer.params, { time: formattedTime });
        const newLayer = new TileLayer({
          title: layer.title,
          source: new TileWMS({
            url: layer.url,
            params: params,
            transition: 0,
          }),
          time: t.clone(),
        });
        time_layer.getLayers().push(newLayer);
      }
    } else if (layer.type === "nws-alerts") {
      const formatter = new GeoJSON({
        dataProjection: NysmMap.latlonProjection,
        featureProjection: this.mapProjection,
      });
      const makeFeatures = (alerts) => {
        if (!alerts) {
          return [];
        }
        return alerts
          .filter((adv) => adv.geometry !== null)
          .reverse()
          .map((adv) => {
            const feature = new Feature({
              geometry: formatter.readGeometry(adv.geometry),
              type: "nws_alert",
              alert: adv,
              ...adv.properties,
            });
            return feature;
          });
      };
      const features = makeFeatures(layer.alerts);
      const source = new VectorSource({
        features: features,
      });
      layer.onNewAlerts = (alerts) => {
        const newFeatures = makeFeatures(alerts);
        source.clear();
        source.addFeatures(newFeatures);
      };
      if (layer.addedToMap) {
        layer.addedToMap();
      }
      const styles = {};
      layer.ol_layer = new VectorLayer({
        title: layer.title,
        source: source,
        style: (feature, resolution) => {
          const props = feature.getProperties();
          const { event, plotting } = props;
          let color = plotting && plotting.hex ? plotting.hex : null;
          if (color === null) {
            console.warn("No color for event " + event);
            color = "#FFFFFF";
          }
          // RGBA hex does not work in Edge or on various smart TVs, convert to rgba() syntax
          color = d3color(color);
          if (!styles.hasOwnProperty(color)) {
            styles[color] = new Style({
              fill: new Fill({
                color: color.copy({ opacity: 0.6 }).formatRgb(),
              }),
              stroke: new Stroke({
                color: color.formatRgb(),
                width: 2,
              }),
            });
          }
          return styles[color];
        },
      });
      layer.group.getLayers().push(layer.ol_layer);
    } else if (layer.type === "mapbox-gl") {
      const mbglFactory = new MbGlLayerFactory();
      let resolver = null;
      layer.mbgl = new Promise((res, rej) => (resolver = res));
      layer.mbgl_cancel = { current: false };
      mbglFactory
        .makeLayer({
          map: this._map,
          style: layer.style,
          cancel: layer.mbgl_cancel
        })
        .then((ol_layer) => {
          if (!ol_layer) {
            return;
          }
          layer.ol_layer = ol_layer;
          layer.group.getLayers().push(layer.ol_layer);
          resolver(mbglFactory.mbMap);
          this._cleanups.push(() => {
            mbglFactory.cleanup();
          });
          this._resizeActions.push(() => {
            mbglFactory.handleResize();
          });
        });
    } else if (layer.type === "multi") {
      layer.layers.forEach(this.addLayer);
    }
  }

  removeLayer(layer) {
    this._clearFaders(layer);
    if (layer.type === "multi") {
      layer.layers.forEach(this.removeLayer);
    }
    if (layer.mbgl) {
      layer.mbgl_cancel.current = true;
    }
    if (layer.hasOwnProperty("ol_layer")) {
      if (layer.group.getLayers().getArray().indexOf(layer.ol_layer) !== -1) {
        const removed = layer.group.getLayers().remove(layer.ol_layer);

        if (removed) {
          if (removed instanceof TimeLayer) {
            layer.ol_layer.removedFromMap();
          }
          const legend = removed.get("legend");
          if (legend && this._legendTarget) {
            legend.remove(this._legendTarget);
          }
        }
      }
    }
  }

  fadeLayer = (oldLayer, newLayer, durationMs = 1000) => {
    // Unimplemented: fading from/to null
    if (oldLayer === null || newLayer === null) {
      if (newLayer !== null) {
        this.addLayer(newLayer);
      }
      if (oldLayer !== null) {
        this.removeLayer(oldLayer);
      }
      return;
    }

    // Put layers in common groups
    let oldLayers = oldLayer.type === "multi" ? oldLayer.layers : [oldLayer];
    let newLayers = newLayer.type === "multi" ? newLayer.layers : [newLayer];
    let allGroups = [
      ...oldLayers.map((l) => l.group),
      ...newLayers.map((l) => l.group),
    ];
    allGroups = allGroups.filter((v, i, self) => self.indexOf(v) === i); // remove duplicates
    const fadeGroups = [];
    allGroups.forEach((g) => {
      const layersInGroup = { group: g };
      layersInGroup.old = oldLayers.find((l) => l.group === g);
      layersInGroup.new = newLayers.find((l) => l.group === g);
      fadeGroups.push(layersInGroup);
    });

    this._legendTarget &&
      oldLayers.forEach(
        (l) =>
          l.ol_layer.get("legend") &&
          l.ol_layer.get("legend").remove(this._legendTarget)
      );
    this.addLayer(newLayer); // Always adds in front
    fadeGroups.forEach((g) => {
      // Set new layer to visible, 1.0 opacity
      g.new.ol_layer.setOpacity(1.0);

      if (g.old && g.new) {
        // Move new layer behind old layer
        const groupLayers = g.group.getLayers();
        const oldIndex = groupLayers.getArray().indexOf(g.old.ol_layer);
        const newIndex = groupLayers.getArray().indexOf(g.new.ol_layer);
        if (oldIndex < newIndex) {
          // swap places
          groupLayers.removeAt(oldIndex);
          groupLayers.insertAt(newIndex, g.old.ol_layer);
        }

        // Fade old layer to 0.0 opacity
        let fader = this._fader(g.old.ol_layer, 1, 0, Math.floor(durationMs));
        this._faders.push({ layer: g.old, finish: fader.finish });
        fader.promise.then((cancelled) => {
          if (!cancelled) {
            this.removeLayer(g.old);
          }
        });

        // If not a gradient layer, then fade the new layer on.
        if (g.new.type.indexOf("grad") === -1) {
          fader = this._fader(g.new.ol_layer, 0, 1, Math.floor(durationMs));
          this._faders.push({ layer: g.new, finish: fader.finish });
        }
      }
    });
  };

  _clearFaders(layer) {
    const list = this._faders.filter((f) => f.layer === layer);
    list.forEach((f) => f.finish());
    list.forEach((f) => {
      const index = this._faders.indexOf(f);
      if (index !== -1) {
        this._faders.splice(index, 1);
      }
    });
  }

  _fader(ol_layer, start, end, durationMs) {
    const rafAvailable = typeof (window.requestAnimationFrame !== "undefined");
    let startTime = null;
    const delayTime = 100;
    const delta = end - start;
    ol_layer.setOpacity(start);
    const nysmMapClass = this;

    let timeout = null;
    let nextFire = null;
    let stop = false;
    const finisher = () => {
      if (timeout) {
        stop = true;
        if (rafAvailable) {
          window.cancelAnimationFrame(timeout);
        } else {
          clearTimeout(timeout);
        }
        if (nextFire) {
          nextFire();
        }
      }
    };
    const p = new Promise((resolve) => {
      const insideFader = (timestamp) => {
        if (stop) {
          ol_layer.setOpacity(end);
          const doomed = nysmMapClass._faders.find(
            (f) => f.layer.ol_layer && f.layer.ol_layer === ol_layer
          );
          const index = nysmMapClass._faders.indexOf(doomed);
          if (index !== -1) {
            nysmMapClass._faders.splice(index, 1);
          }
          resolve(true);
          return;
        }

        if (startTime === null) {
          startTime = rafAvailable ? timestamp : new Date();
        }
        const elapsed = Math.min(
          durationMs,
          rafAvailable ? timestamp - startTime : new Date() - startTime
        );
        const opacityNeeded = start + (elapsed / durationMs) * delta;
        ol_layer.setOpacity(opacityNeeded);
        let finished = elapsed >= durationMs;
        if (!finished) {
          nextFire = insideFader;
          if (rafAvailable) {
            timeout = window.requestAnimationFrame(nextFire);
          } else {
            timeout = setTimeout(nextFire, delayTime);
          }
        } else {
          nextFire = null;
          const doomed = nysmMapClass._faders.find(
            (f) => f.layer.ol_layer && f.layer.ol_layer === ol_layer
          );
          const index = nysmMapClass._faders.indexOf(doomed);
          if (index !== -1) {
            nysmMapClass._faders.splice(index, 1);
          }
          resolve(false);
          return;
        }
      };
      nextFire = insideFader;
      if (rafAvailable) {
        timeout = window.requestAnimationFrame(nextFire);
      } else {
        timeout = setTimeout(nextFire, 0);
      }
    });
    return { promise: p, finish: finisher };
  }

  handleResize() {
    this._resizeActions.forEach((f) => f());
  }

  addNyZoomControls() {
    let zoomControls = new NyZoomControl({
      mapProjection: this.mapProjection,
      regions: [
        {
          label: "New York State",
          extents: [
            [-79.79, 40.48],
            [-71.84, 45.02],
          ],
        },
        {
          label: "Western NY",
          extents: [
            [-79.79, 42],
            [-77.63, 43.42],
          ],
        },
        {
          label: "Central NY",
          extents: [
            [-78.45, 42.05],
            [-75.36, 43.38],
          ],
        },
        {
          label: "North Country",
          extents: [
            [-76.83, 43.05],
            [-73.28, 45.02],
          ],
        },
        {
          label: "Capital District",
          extents: [
            [-75.31, 42.07],
            [-73.51, 43.34],
          ],
        },
        {
          label: "Hudson Valley",
          extents: [
            [-75.36, 40.99],
            [-73.65, 42.03],
          ],
        },
        {
          label: "New York City",
          extents: [
            [-74.26, 40.48],
            [-73.6, 41],
          ],
        },
        {
          label: "Long Island",
          extents: [
            [-73.87, 40.6],
            [-71.79, 41.3],
          ],
        },
      ],
    });
    this._map.addControl(zoomControls);
  }

  addFullScreenControl() {
    this._map.addControl(new FullScreen({ className: "ol-full-screen-left" }));
  }

  // blockAccidentalPanZoom() {
  //   this._map.getInteractions().forEach(interaction => {
  //     if (interaction instanceof DragPan || interaction instanceof MouseWheelZoom) {
  //       interaction.setActive(false);
  //     }
  //   });
  //   this._map.addInteraction(new DragPan({
  //     condition: function(olBrowserEvent) {
  //       if (olBrowserEvent.originalEvent.touches) {
  //         return olBrowserEvent.originalEvent.touches.length > 1;
  //       }
  //       return true;
  //     }
  //   }));
  //   this._map.addInteraction(new MouseWheelZoom({
  //     condition: function (olBrowserEvent) {
  //       return olBrowserEvent.originalEvent.ctrlKey;
  //     }
  //   }));
  // }

  // exportPng() { // needs to be rewritten, now that ol is not guaranteed to render to canvas
  //   this._map.once("postcompose", function(event) {
  //     let canvas = event.context.canvas;
  //     if (navigator.msSaveBlob) {
  //       navigator.msSaveBlob(canvas.msToBlob(), "map.png");
  //     } else {
  //       canvas.toBlob(function(blob) {
  //         saveAs(blob, "map.png");
  //       });
  //     }
  //   });
  //   this._map.renderSync();
  // }

  get map() {
    return this._map;
  }

  get timeManager() {
    return this._timeManager;
  }

  get mapProjection() {
    return "EPSG:3857";
  }

  static get latlonProjection() {
    return "EPSG:4326";
  }

  get nysExtent() {
    return transform(
      [-79.79, 40.48],
      NysmMap.latlonProjection,
      this.mapProjection
    ).concat(
      transform([-71.84, 45.03], NysmMap.latlonProjection, this.mapProjection)
    );
  }

  get nycExtent() {
    return transform(
      [-74.26, 40.48],
      NysmMap.latlonProjection,
      this.mapProjection
    ).concat(
      transform([-73.6, 41], NysmMap.latlonProjection, this.mapProjection)
    );
  }

  destroy() {
    this._cleanups.reverse(); // makes changes inplace
    this._cleanups.forEach((f) => f());
  }
}
